import { MetricGrouping, MetricIntervalPeriod } from '@/types/agentEvents';
import {
  defaultHourRange,
  defaultQueueMetricsParams,
} from '@/utils/metricsParams';
import {
  meanMetricValue,
  formatSecondsToHHMMSS,
} from '@/utils/metricsAggregate';
import { MetricsSection } from './MetricsSection';
import { IntervalMetricsChartWithSeries } from '@/components/common/IntervalMetricsChartWithSeries';
import { MetricCard } from '@/components/common/MetricCard';
import { api } from '@/store/api';
import { Box } from '@gapartners/ui';
import { useTranslation } from 'react-i18next';

const { useGetAverageHoldTimeMetricsQuery } = api;

const timeStringToSeconds = (v: number | string): number => {
  if (typeof v === 'number') return v;
  const s = String(v);
  const match = s.match(/^(\d+):(\d+):(\d+)$/);
  if (match)
    return (
      parseInt(match[1], 10) * 3600 +
      parseInt(match[2], 10) * 60 +
      parseInt(match[3], 10)
    );
  return 0;
};

export function AverageHoldTimeSection() {
  const { t } = useTranslation();

  return (
    <MetricsSection
      titleKey="components.dashboard.averageHoldTime"
      descriptionKey="components.dashboard.averageHoldTimeDescription"
      metricName="average-hold-time"
      defaultParams={{
        ...defaultQueueMetricsParams(),
        ...defaultHourRange(),
        groupings: [MetricGrouping.AGENT],
        intervalPeriod: MetricIntervalPeriod.HOUR,
      }}
      useQuery={useGetAverageHoldTimeMetricsQuery}
      refreshLabelKey="components.dashboard.refreshAverageHoldTime"
      renderData={({ data, params, groupingKey }) => (
        <Box>
          {/* Team Average Card */}
          <Box sx={{ mb: 2 }}>
            <MetricCard
              title={t('components.dashboard.teamAverage')}
              value={formatSecondsToHHMMSS(
                meanMetricValue(data, timeStringToSeconds)
              )}
            />
          </Box>

          {/* Chart or Grid */}
          {params.intervalPeriod !== MetricIntervalPeriod.TOTAL &&
          data.some(item => item.intervalStartTime != null) ? (
            <IntervalMetricsChartWithSeries
              data={data}
              groupingKey={groupingKey}
              valueToNumber={timeStringToSeconds}
              valueFormatter={v => (typeof v === 'string' ? v : String(v))}
            />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                  xl: 'repeat(5, 1fr)',
                },
                gap: 2,
              }}
            >
              {data.map(item => {
                const displayValue =
                  typeof item.value === 'string'
                    ? item.value
                    : String(item.value);
                return (
                  <MetricCard
                    key={item.id}
                    title={item.dimensionLabels?.[groupingKey] ?? item.name}
                    value={displayValue}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}
    />
  );
}
